import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Sparkles, TrendingUp, TrendingDown, Clock, ArrowRight } from "lucide-react";

/**
 * Buyer-facing loyalty ledger. Sourced from the same `/api/account/loyalty`
 * endpoint used by the checkout redeem widget so the numbers are always in
 * sync with what the buyer sees when spending points.
 *
 * The page is intentionally read-only — every mutation happens through the
 * order lifecycle (earn on approval, redeem at checkout). If the buyer sees
 * a number that surprises them here, they can trace it back to the exact
 * order that caused it via the linked order number.
 */
export default function AccountPoints() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/account/loyalty")
      .then((r) => setData(r.data))
      .catch((e) => setErr(e.response?.data?.detail || e.message));
  }, []);

  if (err) return <div className="text-[var(--crimson)]" data-testid="points-page-error">Failed to load: {err}</div>;
  if (!data) return <div className="text-sm font-mono uppercase tracking-wider text-[var(--ink-muted)]">Loading loyalty ledger…</div>;

  const { balance, balance_usd, earn_rate_per_dollar, points_per_dollar_redeem, expiry_months, expiring_soon_pts, history } = data;

  return (
    <div className="space-y-8" data-testid="account-points-page">
      <div>
        <div className="eyebrow flex items-center gap-2"><Sparkles className="h-3.5 w-3.5" /> Loyalty points</div>
        <h2 className="font-display text-3xl md:text-4xl mt-1 leading-[1.05]">
          {balance.toLocaleString()} pts
          <span className="ml-2 text-[var(--ink-muted)] font-normal font-mono text-base align-middle">≈ ${balance_usd.toFixed(2)}</span>
        </h2>
        <p className="text-sm text-[var(--ink-soft)] mt-2 max-w-2xl leading-relaxed">
          Earn <span className="font-mono text-[var(--ink)]">{earn_rate_per_dollar} pts</span> per $1 spent (once each order is approved).
          Redeem <span className="font-mono text-[var(--ink)]">{points_per_dollar_redeem} pts</span> for $1 off at checkout.
          Points expire {expiry_months} months after they land.
        </p>
      </div>

      {expiring_soon_pts > 0 && (
        <div
          className="border border-[var(--crimson)]/40 bg-[var(--crimson)]/10 rounded-md px-4 py-3 flex items-start gap-3"
          data-testid="points-expiring-banner"
        >
          <Clock className="h-4 w-4 text-[var(--crimson)] shrink-0 mt-0.5" />
          <div className="text-sm">
            <div className="text-[var(--crimson)] font-semibold">{expiring_soon_pts.toLocaleString()} pts expire in the next 30 days.</div>
            <div className="text-[var(--ink-soft)] mt-1">
              That's ${(expiring_soon_pts / points_per_dollar_redeem).toFixed(2)} of savings you can lock in.{" "}
              <Link to="/shop" className="underline underline-offset-2 hover:text-[var(--terra)]">Shop now →</Link>
            </div>
          </div>
        </div>
      )}

      {/* Quick stats — issued vs redeemed on this account. Buyers love seeing
          how much they've saved lifetime, so we tally it client-side. */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <MiniStat
          icon={<TrendingUp className="h-4 w-4 text-[var(--terra)]" />}
          label="Total earned"
          value={`${sumHistory(history, "earn", "points").toLocaleString()} pts`}
          testid="points-total-earned"
        />
        <MiniStat
          icon={<TrendingDown className="h-4 w-4 text-[var(--crimson)]" />}
          label="Total redeemed"
          value={`${Math.abs(sumHistory(history, "redeem", "points")).toLocaleString()} pts`}
          sub={`= $${(Math.abs(sumHistory(history, "redeem", "points")) / points_per_dollar_redeem).toFixed(2)} saved`}
          testid="points-total-redeemed"
        />
        <MiniStat
          icon={<Sparkles className="h-4 w-4 text-[var(--terra)]" />}
          label="Current balance"
          value={`${balance.toLocaleString()} pts`}
          sub={`≈ $${balance_usd.toFixed(2)} off next order`}
          testid="points-current-balance"
        />
      </div>

      {/* Ledger — every event, newest first. */}
      <section>
        <div className="flex items-baseline justify-between mb-3">
          <h3 className="font-display text-xl font-medium">History</h3>
          <span className="font-mono text-[10.5px] uppercase tracking-[0.18em] text-[var(--ink-muted)]">{history.length} events</span>
        </div>
        {history.length === 0 ? (
          <div className="border border-dashed border-[var(--line)] rounded-md p-8 text-center text-[var(--ink-soft)]" data-testid="points-empty">
            <Sparkles className="h-8 w-8 mx-auto text-[var(--ink-muted)]" strokeWidth={1.4} />
            <div className="mt-3 font-display text-lg text-[var(--ink)]">No loyalty activity yet.</div>
            <div className="text-sm mt-1">Place your first order to start earning {earn_rate_per_dollar} pts / $1.</div>
            <Link to="/shop" className="btn-primary inline-flex mt-4" data-testid="points-empty-shop">
              Shop the catalog <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        ) : (
          <div className="border border-[var(--line)] rounded-md overflow-hidden bg-[var(--paper-2)]">
            <table className="w-full text-sm" data-testid="points-ledger">
              <thead className="bg-[var(--paper-3)] text-[10.5px] uppercase tracking-[0.16em] text-[var(--ink-muted)] font-mono">
                <tr>
                  <th className="text-left px-4 py-2.5">Date</th>
                  <th className="text-left px-4 py-2.5">Type</th>
                  <th className="text-left px-4 py-2.5">Order</th>
                  <th className="text-left px-4 py-2.5 hidden md:table-cell">Note</th>
                  <th className="text-right px-4 py-2.5">Points</th>
                  <th className="text-right px-4 py-2.5">USD</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--line)]">
                {history.map((e) => {
                  const isRedeem = e.kind === "redeem";
                  const pts = Number(e.points || 0);
                  const usd = Math.abs(pts) / points_per_dollar_redeem;
                  return (
                    <tr key={e.id} className="hover:bg-[var(--paper-3)]" data-testid={`points-row-${e.id}`}>
                      <td className="px-4 py-2.5 font-mono text-[11.5px] text-[var(--ink-muted)] whitespace-nowrap">
                        {new Date(e.created_at).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "2-digit" })}
                      </td>
                      <td className="px-4 py-2.5">
                        <span className={`inline-block px-2 py-0.5 rounded-full font-mono text-[10px] uppercase tracking-wider ${isRedeem ? "bg-[var(--crimson)]/15 text-[var(--crimson)]" : "bg-[var(--terra)]/15 text-[var(--terra)]"}`}>
                          {e.kind}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 font-mono text-[11.5px]">
                        {e.order_number ? (
                          <Link
                            to={`/order/${e.order_number}`}
                            className="hover:text-[var(--terra)] underline underline-offset-2"
                          >
                            {e.order_number}
                          </Link>
                        ) : "—"}
                      </td>
                      <td className="px-4 py-2.5 text-[12.5px] text-[var(--ink-soft)] hidden md:table-cell">{e.note || ""}</td>
                      <td className={`px-4 py-2.5 text-right font-mono ${isRedeem ? "text-[var(--crimson)]" : "text-[var(--terra)]"}`}>
                        {isRedeem ? "−" : "+"}{Math.abs(pts).toLocaleString()}
                      </td>
                      <td className={`px-4 py-2.5 text-right font-mono ${isRedeem ? "text-[var(--crimson)]" : "text-[var(--ink-muted)]"}`}>
                        {isRedeem ? "−" : ""}${usd.toFixed(2)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

function MiniStat({ icon, label, value, sub, testid }) {
  return (
    <div className="border border-[var(--line)] rounded-md bg-[var(--paper-2)] p-4" data-testid={testid}>
      <div className="flex items-center gap-2">
        {icon}
        <div className="font-mono text-[10.5px] uppercase tracking-[0.16em] text-[var(--ink-muted)]">{label}</div>
      </div>
      <div className="mt-2 font-display text-xl font-semibold">{value}</div>
      {sub && <div className="mt-0.5 text-[11.5px] text-[var(--ink-soft)]">{sub}</div>}
    </div>
  );
}

function sumHistory(history, kind, field) {
  return (history || []).reduce((acc, e) => e.kind === kind ? acc + Number(e[field] || 0) : acc, 0);
}
